import { useEffect, useRef, useCallback, useState } from 'react';

type MessageHandler = (msg: any) => void;

interface UseGameSocketResult {
  send: (msg: any) => void;
  connected: boolean;
}

export function useGameSocket(onMessage: MessageHandler): UseGameSocketResult {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef<MessageHandler>(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    let destroyed = false;
    let timer: ReturnType<typeof setTimeout>;

    function connect() {
      if (destroyed) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let ws: WebSocket;
      try {
        ws = new WebSocket(`${proto}//${window.location.host}/ws`);
      } catch {
        timer = setTimeout(connect, 3000);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        timer = setTimeout(connect, 2000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (evt) => {
        try {
          onMessageRef.current(JSON.parse(evt.data));
        } catch {}
      };
    }

    connect();
    return () => {
      destroyed = true;
      clearTimeout(timer);
      wsRef.current?.close();
    };
  }, []);

  const send = useCallback((msg: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  return { send, connected };
}
